/**
 * なりすまし中バナー (SA-2 §4.6).
 *
 * /admin/me の impersonatedBy claim を参照し、存在すれば画面上部に警告表示。
 * 「管理者に戻る」ボタンで POST /admin/impersonate/end を呼び、backup トークンを復元。
 * AppLayout / AdminLayout のいずれからも呼び出される想定。
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { API_BASE } from '../lib/api-client.js';

interface AdminMe {
  userId: string;
  saRole: 'admin' | 'operator' | 'auditor' | null;
  impersonatedBy: string | null;
  readOnly: boolean;
}

export function ImpersonateBanner() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [ending, setEnding] = useState(false);

  // admin/me は CSRF 不要 (GET) なのでどのレイアウトからも安全に叩ける
  // 失敗時は null (通常ユーザー、未認証) — バナーは出さない
  const { data } = useQuery({
    queryKey: ['admin', 'me'] as const,
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/admin/me`, { credentials: 'include' });
      if (!res.ok) return null;
      return (await res.json()) as AdminMe;
    },
    staleTime: 30_000,
    retry: false,
  });

  if (!data?.impersonatedBy) return null;

  const handleEnd = async () => {
    setEnding(true);
    try {
      const res = await fetch(`${API_BASE}/admin/impersonate/end`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        alert(body.message ?? 'なりすまし解除に失敗しました');
        // 410 等 backup 不在時はログイン画面へ
        if (res.status === 410) {
          void navigate({ to: '/login' });
          return;
        }
        return;
      }
      await queryClient.invalidateQueries({ queryKey: ['admin', 'me'] });
      void navigate({ to: '/dashboard' });
    } finally {
      setEnding(false);
    }
  };

  return (
    <div
      role="alert"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 500,
        backgroundColor: '#7c3aed',
        color: '#fff',
        padding: '10px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        fontSize: 14,
        fontWeight: 600,
      }}
    >
      <span>
        なりすまし中 (元管理者:{' '}
        <code style={{ fontFamily: 'monospace' }}>{data.impersonatedBy}</code>)
        {data.readOnly && ' ・ 読み取り専用'}
      </span>
      <button
        type="button"
        onClick={() => void handleEnd()}
        disabled={ending}
        style={{
          padding: '6px 14px',
          borderRadius: 6,
          border: '1px solid rgba(255,255,255,0.5)',
          backgroundColor: 'transparent',
          color: '#fff',
          fontSize: 13,
          fontWeight: 600,
          cursor: ending ? 'not-allowed' : 'pointer',
        }}
      >
        {ending ? '解除中...' : '管理者に戻る'}
      </button>
    </div>
  );
}
